import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCustomerAuth } from '@/components/customer/CustomerAuthProvider';

interface PWAInstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PortalPWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  canInstall: boolean;
  isIOS: boolean;
  isMobile: boolean;
  isPWACapable: boolean;
  hasServiceWorker: boolean;
  hasManifest: boolean;
  isSecure: boolean;
  promptInstall: () => Promise<boolean>;
  dismissInstall: () => void;
  isDismissed: boolean;
  forceUpdate: () => Promise<void>;
  checkForUpdates: () => Promise<void>;
  debugInfo?: any;
}

export function usePortalPWA(): PortalPWAState {
  const location = useLocation();
  const { customer } = useCustomerAuth();
  const [deferredPrompt, setDeferredPrompt] = useState<PWAInstallPrompt | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasServiceWorker, setHasServiceWorker] = useState(false);
  const [hasManifest, setHasManifest] = useState(false);
  const [isPWACapable, setIsPWACapable] = useState(false);
  
  // All routes in sposa-suite-portal are "portal" — always active.
  const isPortalRoute = true;
  
  // Check if browser supports PWA installation
  const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  
  // Debug logging - always enabled for PWA debugging
  const debugPWA = (message: string, data?: any) => {
    console.log(`[Portal PWA] ${message}`, data || '');
    
    // Enhanced debugging for critical PWA states
    if (message.includes('PWA Readiness') || message.includes('Install prompt')) {
      console.log(`[Portal PWA Debug] Enhanced state:`, {
        isInstallable,
        isInstalled,
        hasServiceWorker: !!navigator.serviceWorker,
        hasManifest: !!document.querySelector('link[rel="manifest"]'),
        isSecure: window.location.protocol === 'https:' || window.location.hostname === 'localhost',
        userAgent: navigator.userAgent,
        pathname: window.location.pathname,
        isPortalRoute: true,
        deferredPrompt: !!deferredPrompt,
        displayMode: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser'
      });
      
      // Check service worker status
      if (navigator.serviceWorker) {
        navigator.serviceWorker.ready.then(registration => {
          console.log('[Portal PWA] Service worker ready:', {
            active: !!registration.active,
            scope: registration.scope,
            controlling: !!navigator.serviceWorker.controller
          });
        });
      }
    }
  };

  // Effect to manage manifest based on route with cache-busting
  useEffect(() => {
    const manifestLink = document.getElementById('manifest-link') as HTMLLinkElement;
    const portalManifestLink = document.getElementById('portal-manifest-link') as HTMLLinkElement;
    const staffManifestLink = document.getElementById('staff-manifest-link') as HTMLLinkElement;
    
    if (isPortalRoute) {
      debugPWA('🎯 Portal route detected, ensuring portal manifest is active');
      
      // Force cache-busting for manifest refresh
      const cacheBuster = `?v=${Date.now()}`;
      const portalManifestUrl = `/manifest.json${cacheBuster}`;
      
      if (manifestLink) {
        // Update main manifest link to portal manifest with cache-busting
        manifestLink.href = portalManifestUrl;
        manifestLink.disabled = false;
        
        debugPWA('✅ Updated main manifest link to:', portalManifestUrl);
      }
      
      // Disable other manifest links
      if (portalManifestLink) portalManifestLink.disabled = true;
      if (staffManifestLink) staffManifestLink.disabled = true;
      
      // Force browser to re-evaluate manifest
      setTimeout(() => {
        const event = new CustomEvent('manifestchange');
        window.dispatchEvent(event);
      }, 100);
      
      // Validate manifest exists and is accessible
      fetch(portalManifestUrl)
        .then(response => {
          if (response.ok) {
            setHasManifest(true);
            debugPWA('Portal manifest validated successfully');
            return response.json();
          }
          throw new Error(`Manifest fetch failed: ${response.status}`);
        })
        .then(manifestData => {
          debugPWA('Portal manifest content', manifestData);
        })
        .catch(error => {
          debugPWA('Portal manifest validation failed', error);
          setHasManifest(false);
        });
      
    } else {
      debugPWA('🏢 Staff route detected, ensuring main manifest is active');
      
      // Force cache-busting for manifest refresh
      const cacheBuster = `?v=${Date.now()}`;
      const staffManifestUrl = `/manifest.json${cacheBuster}`;
      
      if (manifestLink) {
        // Update main manifest link to staff manifest with cache-busting
        manifestLink.href = staffManifestUrl;
        manifestLink.disabled = false;
        
        debugPWA('✅ Updated main manifest link to:', staffManifestUrl);
      }
      
      // Disable other manifest links
      if (portalManifestLink) portalManifestLink.disabled = true;
      if (staffManifestLink) staffManifestLink.disabled = true;
      
      setHasManifest(true);
    }
  }, [isPortalRoute]);

  // Single event listener registration on mount - avoid multiple listeners
  useEffect(() => {
    // Check if app is already installed
    const checkInstalled = () => {
      const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
      const isInWebAppMode = (window.navigator as any).standalone === true;
      const isInstalled = isInStandaloneMode || isInWebAppMode;
      setIsInstalled(isInstalled);
      debugPWA('Installation check', { isInStandaloneMode, isInWebAppMode, isInstalled });
    };

    // Check if previously dismissed (per session)
    const checkDismissed = () => {
      const dismissed = sessionStorage.getItem('portal-pwa-dismissed');
      setIsDismissed(dismissed === 'true');
      debugPWA('Dismissed check', { dismissed });
    };

    // Check PWA readiness
    const checkPWAReadiness = () => {
      const serviceWorkerSupported = 'serviceWorker' in navigator;
      const manifestElement = document.querySelector('link[rel="manifest"]');
      const pwaCapable = isPWACapableBrowser();
      
      setHasServiceWorker(serviceWorkerSupported);
      // Don't set hasManifest here, it's set in the fetch validation above
      setIsPWACapable(pwaCapable);
      
      debugPWA('PWA Readiness', { 
        hasServiceWorker: serviceWorkerSupported, 
        isSecure, 
        hasManifest: !!manifestElement,
        manifestHref: manifestElement?.getAttribute('href'),
        isPWACapable: pwaCapable,
        isPortalRoute,
        currentPath: location.pathname,
        userAgent: navigator.userAgent
      });
    };

    checkInstalled();
    checkDismissed();
    checkPWAReadiness();

    // Listen for the beforeinstallprompt event - only register once
    const handleBeforeInstallPrompt = (e: Event) => {
      debugPWA('Before install prompt triggered', { 
        isPortalRoute, 
        hasCustomerOrg: !!customer?.organization_id,
        eventType: e.type,
        eventIsTrusted: e.isTrusted 
      });
      
      // Only handle trusted browser events, not artificial ones
      if (!e.isTrusted) {
        debugPWA('Ignoring untrusted beforeinstallprompt event');
        return;
      }
      
      e.preventDefault();
      
      // Set as installable if we're on a portal route
      if (isPortalRoute) {
        const promptEvent = e as PWAInstallPrompt;
        // Validate the event has the required methods
        if (typeof promptEvent.prompt === 'function') {
          setDeferredPrompt(promptEvent);
          setIsInstallable(true);
          debugPWA('Install prompt ready and validated');
        } else {
          debugPWA('Install prompt event missing required methods');
        }
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      debugPWA('App installed successfully');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      sessionStorage.removeItem('portal-pwa-dismissed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []); // Empty dependency array - register listeners only once

  // Register service worker once when needed - prevent multiple registrations
  useEffect(() => {
    if (isPortalRoute && 'serviceWorker' in navigator) {
      // Check if already registered to avoid duplicates
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        const portalSW = registrations[0];
        if (!portalSW) {
          debugPWA('Registering service worker');
          navigator.serviceWorker.register('/portal-sw.js', {
            scope: '/'
          }).then((registration) => {
            debugPWA('Portal SW registered successfully', { 
              scope: registration.scope,
              active: !!registration.active,
              state: registration.active?.state
            });
            setHasServiceWorker(true);
            
            // Listen for service worker updates
            registration.addEventListener('updatefound', () => {
              debugPWA('Service worker update found');
              const newWorker = registration.installing;
              
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    debugPWA('New service worker installed, update available');
                    // Could show update notification here
                  }
                });
              }
            });
            
          }).catch((error) => {
            debugPWA('Portal SW registration failed', error);
            setHasServiceWorker(false);
          });
        } else {
          debugPWA('Portal SW already registered', { 
            scope: portalSW.scope,
            active: !!portalSW.active,
            state: portalSW.active?.state
          });
          setHasServiceWorker(true);
        }
      }).catch((error) => {
        debugPWA('Service worker registration check failed', error);
        setHasServiceWorker(false);
      });
      
      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        debugPWA('Message from SW:', event.data);
        
        if (event.data.type === 'UPDATE_COMPLETE') {
          debugPWA('Service worker update completed');
          // Could show success message
        }
        
        if (event.data.type === 'RELOAD_FOR_UPDATE') {
          debugPWA('Reloading for update');
          window.location.reload();
        }
      });
      
    }
  }, [isPortalRoute]);


  const promptInstall = async (): Promise<boolean> => {
    debugPWA('Install prompt triggered', { 
      hasDeferredPrompt: !!deferredPrompt,
      promptType: typeof deferredPrompt?.prompt,
      isPortalRoute,
      userAgent: navigator.userAgent,
      isSecure,
      serviceWorkerReady: !!navigator.serviceWorker?.controller
    });
    
    if (!deferredPrompt) {
      debugPWA('No deferred prompt available - checking PWA requirements');
      
      // Additional debugging when no prompt is available
      const manifest = document.querySelector('link[rel="manifest"]');
      debugPWA('Manifest link present', { present: !!manifest });
      
      if (manifest) {
        try {
          const response = await fetch(manifest.getAttribute('href') || '');
          const manifestData = await response.json();
          debugPWA('Current manifest', manifestData);
        } catch (err) {
          debugPWA('Failed to fetch manifest', { error: err });
        }
      }
      
      return false;
    }

    // Validate the prompt object has required methods before using
    if (typeof deferredPrompt.prompt !== 'function') {
      debugPWA('Deferred prompt missing prompt() method', { 
        promptObj: deferredPrompt,
        promptType: typeof deferredPrompt.prompt
      });
      // Clear invalid prompt and reset state
      setDeferredPrompt(null);
      setIsInstallable(false);
      return false;
    }

    try {
      debugPWA('Calling deferredPrompt.prompt()');
      await deferredPrompt.prompt();
      
      debugPWA('Waiting for user choice');
      const choiceResult = await deferredPrompt.userChoice;
      
      debugPWA('User choice result', choiceResult);
      
      if (choiceResult.outcome === 'accepted') {
        debugPWA('User accepted install');
        setIsInstallable(false);
        setDeferredPrompt(null);
        return true;
      } else {
        debugPWA('User dismissed install');
        return false;
      }
    } catch (error) {
      debugPWA('Error during install prompt', error);
      // Reset state on error
      setDeferredPrompt(null);
      setIsInstallable(false);
      return false;
    }
  };

  const dismissInstall = () => {
    setIsDismissed(true);
    sessionStorage.setItem('portal-pwa-dismissed', 'true');
  };

  // Force update function
  const forceUpdate = async (): Promise<void> => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      debugPWA('Triggering force update');
      navigator.serviceWorker.controller.postMessage({ type: 'FORCE_UPDATE' });
      
      // Also clear browser cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            if (cacheName.startsWith('portal-')) {
              return caches.delete(cacheName);
            }
          })
        );
      }
      
      // Reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  // Check for updates function
  const checkForUpdates = async (): Promise<void> => {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const portalSW = registrations[0];

      if (portalSW) {
        await portalSW.update();
        debugPWA('Checked for service worker updates');
      }
    }
  };

  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  };

  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Check if browser is PWA capable (supports installation)
  const isPWACapableBrowser = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Chrome, Edge, Samsung Internet, Opera support PWA on both mobile and desktop
    const chromeFamily = /chrome|chromium|edg\/|samsungbrowser|opr\//.test(userAgent);
    
    // Safari supports PWA on iOS and macOS (with some limitations)
    const safariFamily = /safari/.test(userAgent) && !/chrome/.test(userAgent);
    
    // Firefox has limited PWA support but can install some PWAs
    const firefoxFamily = /firefox/.test(userAgent);
    
    return chromeFamily || safariFamily || firefoxFamily;
  };

  return {
    isInstallable: isInstallable && isPortalRoute && !isDismissed,
    isInstalled,
    promptInstall,
    dismissInstall,
    isDismissed,
    isIOS: isIOS(),
    isMobile: isMobile(),
    isPWACapable,
    hasServiceWorker,
    hasManifest,
    isSecure,
    // Show install option if: browser is PWA capable, not installed, on portal route, not dismissed
    canInstall: isPWACapable && !isInstalled && isPortalRoute && !isDismissed && (isInstallable || isIOS()),
    forceUpdate,
    checkForUpdates,
    debugInfo: {
      userAgent: navigator.userAgent,
      currentPath: location.pathname,
      isStandalone: window.matchMedia('(display-mode: standalone)').matches
    }
  };
}